import { Component, OnDestroy, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { OpsService } from '../../core/services/ops.service';
import { ThemeService } from '../../core/services/theme.service';

type LoginMode = 'password' | 'otp';
type OtpStep = 'request' | 'verify';
type OtpChannel = 'email' | 'mobile';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnDestroy {
  readonly theme = inject(ThemeService);
  private readonly auth = inject(AuthService);
  private readonly ops = inject(OpsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  mode: LoginMode = 'password';
  otpStep: OtpStep = 'request';
  otpChannel: OtpChannel = 'email';

  username = '';
  password = '';
  /** Full email or mobile typed by the user for OTP delivery check. */
  otpDestination = '';
  otp = '';
  challengeId = '';
  destinationMasked = '';

  /** Seconds left until OTP expires (countdown). */
  readonly otpExpiresIn = signal(0);
  /** Seconds until Resend is allowed. */
  readonly resendIn = signal(0);

  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(
    this.route.snapshot.queryParamMap.get('reason') === 'session'
      ? 'Session expired or invalid. Sign in again.'
      : null,
  );
  readonly busy = signal(false);

  private expireTimer: ReturnType<typeof setInterval> | null = null;

  ngOnDestroy(): void {
    this.clearTimers();
  }

  setMode(next: LoginMode): void {
    this.mode = next;
    this.error.set(null);
    this.info.set(null);
    this.otpStep = 'request';
    this.otp = '';
    this.otpDestination = '';
    this.challengeId = '';
    this.destinationMasked = '';
    this.clearTimers();
  }

  onOtpChannelChange(_channel: OtpChannel): void {
    this.otpDestination = '';
    this.error.set(null);
  }

  formatMmSs(totalSeconds: number): string {
    const s = Math.max(0, Math.floor(totalSeconds));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  }

  async submitAdmin(): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    this.info.set(null);
    const result = await this.auth.loginAdmin(this.username, this.password);
    if (!result.ok) {
      this.busy.set(false);
      this.error.set(result.message);
      return;
    }
    await this.enterPortal();
  }

  async requestOtp(): Promise<void> {
    this.error.set(null);
    this.info.set(null);
    const formatError = this.validateOtpDestination();
    if (formatError) {
      this.error.set(formatError);
      return;
    }
    this.busy.set(true);
    const result = await this.auth.requestOfficerOtp(
      this.otpChannel,
      this.otpDestination.trim(),
    );
    this.busy.set(false);
    if (!result.ok) {
      this.error.set(result.message);
      return;
    }
    this.challengeId = result.challengeId;
    this.destinationMasked = result.destinationMasked;
    this.otpStep = 'verify';
    this.info.set(`OTP sent to ${result.destinationMasked}`);
    this.startOtpTimers(result.expiresInSeconds, result.resendAvailableInSeconds ?? 60);
  }

  /** Client-side format checks before the API call. */
  private validateOtpDestination(): string | null {
    const value = this.otpDestination.trim();
    if (!value) {
      return this.otpChannel === 'email' ? 'Email is required' : 'Mobile number is required';
    }
    if (this.otpChannel === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Enter a valid email address';
      }
      return null;
    }
    const digits = value.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) {
      return 'Enter a valid mobile number';
    }
    return null;
  }

  async resendOtp(): Promise<void> {
    if (!this.challengeId || this.resendIn() > 0) return;
    this.busy.set(true);
    this.error.set(null);
    const result = await this.auth.resendOfficerOtp(this.challengeId);
    this.busy.set(false);
    if (!result.ok) {
      this.error.set(result.message);
      return;
    }
    this.challengeId = result.challengeId;
    this.destinationMasked = result.destinationMasked;
    this.info.set(`OTP resent to ${result.destinationMasked}`);
    this.startOtpTimers(result.expiresInSeconds, result.resendAvailableInSeconds ?? 60);
  }

  async verifyOtp(): Promise<void> {
    if (this.otpExpiresIn() <= 0) {
      this.error.set('OTP expired. Please resend a new code.');
      return;
    }
    this.busy.set(true);
    this.error.set(null);
    const result = await this.auth.verifyOfficerOtp(this.challengeId, this.otp);
    if (!result.ok) {
      this.busy.set(false);
      this.error.set(result.message);
      return;
    }
    this.clearTimers();
    await this.enterPortal();
  }

  backToOtpRequest(): void {
    this.otpStep = 'request';
    this.otp = '';
    this.clearTimers();
  }

  private startOtpTimers(expiresInSeconds: number, resendCooldownSeconds: number): void {
    this.clearTimers();
    const ttl = Math.max(0, Math.floor(Number(expiresInSeconds) || 300));
    const cool = Math.max(0, Math.floor(Number(resendCooldownSeconds) || 60));
    this.otpExpiresIn.set(ttl);
    this.resendIn.set(cool);
    this.expireTimer = setInterval(() => {
      this.otpExpiresIn.update((v) => Math.max(0, v - 1));
      this.resendIn.update((v) => Math.max(0, v - 1));
      if (this.otpExpiresIn() <= 0 && this.resendIn() <= 0 && this.expireTimer) {
        clearInterval(this.expireTimer);
        this.expireTimer = null;
      }
    }, 1000);
  }

  private clearTimers(): void {
    if (this.expireTimer) {
      clearInterval(this.expireTimer);
      this.expireTimer = null;
    }
    this.otpExpiresIn.set(0);
    this.resendIn.set(0);
  }

  private async enterPortal(): Promise<void> {
    try {
      await this.ops.refresh();
    } catch {
      // still enter portal; pages can retry
    }
    this.busy.set(false);
    void this.router.navigateByUrl('/dashboard');
  }
}
